import {
  AndroidLogo,
  AppleLogo,
  ChartBar,
  Info,
  PuzzlePiece,
  SlidersHorizontal,
} from "@phosphor-icons/react";
import { ColumnDef } from "@tanstack/react-table";
import { Badge, Button, Tooltip } from "flowbite-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SingleValue } from "react-select";
import FilterSummary, { ActiveFilter } from "../../../../components/FilterSummary";
import AnimatedModal from "../../../../components/AnimatedModal";
import {
  BannerHeightOffset,
  BannerNote,
} from "../../../../components/BannerNote";
import CustomSwitch from "../../../../components/CustomSwitch";
import DateRangePickerButton from "../../../../components/DateRangePicker/DateRangePickerButton";
import SearchInput from "../../../../components/SearchInput";
import { Table } from "../../../../components/Table";
import { useDateRange } from "../../../../contexts/DateRangeContext";
import {
  useAppChartData,
  useAppListData,
  useAuth,
  useIsMobile,
} from "../../../../hooks";
import {
  IDateRangePreset,
  ISelectOption,
  StatusOptionData,
} from "../../../../interfaces";
import { checkPermission, formatNumber, getNetworkWiseRevenue } from "../../../../lib/utils";
import {
  DEFAULT_ITEMS_PER_PAGE,
  LOCAL_STORAGE_KEYS,
  PERMISSION_MAPPER,
} from "../../../../shared/constants";
import { dynamicConstants } from "../../../../shared/dynamicConstants";
import AppLogo from "../../../AppCountryReports/components/AppLogo";
import AppListFilters from "../../components/AppListFilters";
import AppRevenueChart from "../../components/AppRevenueChart";
import { IAdmobApp } from "../../interface";

interface AppOption extends ISelectOption {
  platform: string;
  appId: string;
  app_logo_url: string;
  app_platform: string;
}


const columnIds = {
  APP_NAME: "app_name",
  ACCOUNT: "account",
  APP_STATUS: "google_admob_app_status",
  ESTIMATED_REVENUE: "actual_estimate_revenue",
  REVENUE_CURRENCY: "revenue_currency",
  IN_APP_PURCHASES_OR_SUBSCRIPTIONS:
    "in_app_purchase.total_in_app_purchase_proceeds",
  TOTAL_COST_DOLLAR_WITHOUT_TAX: "without_tax.total_cost_usd",
  TOTAL_COST_INR_WITHOUT_TAX: "without_tax.total_cost_inr",
  COST_CURRENCY: "cost_currency",
  NET_DOLLAR_WITHOUT_TAX: "without_tax.total_cost_usd_net",
  PERCENTAGE_WITHOUT_TAX: "without_tax.total_cost_usd_net_percentage",
  TOTAL_COST_DOLLAR_WITH_TAX: "with_tax.total_cost_usd",
  TOTAL_COST_WITH_TAX: "with_tax.total_cost_inr",
  NET_DOLLAR_WITH_TAX: "with_tax.total_cost_usd_net",
  PERCENTAGE_WITH_TAX: "with_tax.total_cost_usd_net_percentage",
  ACTIONS: "actions",
};

const withTaxColumns = [
  columnIds.TOTAL_COST_DOLLAR_WITH_TAX,
  columnIds.NET_DOLLAR_WITH_TAX,
  columnIds.TOTAL_COST_WITH_TAX,
  columnIds.PERCENTAGE_WITH_TAX,
];

interface ColumnOption {
  id: string;
  label: string;
  enabled: boolean;
}

const AppList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updatePreferences } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const { DYNAMIC_STATUS_OPTIONS, growthIndicatorRange } = dynamicConstants();
  const [selectedStatus, setSelectedStatus] = useState<
    SingleValue<StatusOptionData> | any
  >(() => {
    const statusParam = searchParams.get("status");
    return statusParam
      ? DYNAMIC_STATUS_OPTIONS.find((opt) => opt.value === statusParam) ||
      DYNAMIC_STATUS_OPTIONS[0]
      : DYNAMIC_STATUS_OPTIONS[0];
  });

  const isMobile = useIsMobile();
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const appListPreference = useMemo(() => user?.preferences?.app_list, [user]);
  const columnConfig = useMemo(
    () => appListPreference?.column_config ?? {},
    [appListPreference]
  );

  const showWithTax = useMemo(
    () =>
      Object.keys(columnConfig).length > 0
        ? Object.entries(columnConfig)
          .filter(([key]) => withTaxColumns.includes(key))
          .every(([key]) => columnConfig[key] === true)
        : false,
    [columnConfig]
  );

  const { dateRange, selectedPreset, setDateRange, setSelectedPreset } =
    useDateRange();

  const [showChartModal, setShowChartModal] = useState(false);
  const [selectedAppForChart, setSelectedAppForChart] =
    useState<IAdmobApp | null>(null);
  const [selectedPresetForChart, setSelectedPresetForChart] = useState<
    IDateRangePreset | undefined
  >(selectedPreset);

  // App filter state - initialize from URL params
  const [selectedApp, setSelectedApp] = useState<AppOption | null>(() => {
    const appSlug = searchParams.get("app");
    return appSlug
      ? {
        label: "",
        value: appSlug,
        platform: "",
        appId: appSlug,
        app_logo_url: "",
        app_platform: "",
      }
      : null;
  });
  const [selectedPlatform, setSelectedPlatform] =
    useState<ISelectOption | null>(() => {
      const platform = searchParams.get("platform");
      if (!platform) return null;
      const allowed = ["android", "ios", "all"];
      if (allowed.includes(platform)) {
        return {
          label:
            platform === "android"
              ? "Android"
              : platform === "ios"
                ? "iOS"
                : platform,
          value: platform,
        };
      }
      return null;
    });

  const [chartDateRange, setChartDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>(() => {
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
  });

  const {
    data: chartData,
    isLoading: isLoadingChartData,
    error: chartError,
  } = useAppChartData({
    appSlug: selectedAppForChart?.app_slug || "",
    startDate: chartDateRange.startDate,
    endDate: chartDateRange.endDate,
  });

  const [openFilter, setOpenFilter] = useState(false);

  // Use the React Query hook to fetch app list data
  const { data: appListData, isLoading } = useAppListData({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  // Extract the data we need from the query result
  const allApps = useMemo(
    () => appListData?.results?.admob_apps || [],
    [appListData]
  );
  const results = useMemo(() => appListData?.results || null, [appListData]);

  // Filter apps based on search query, status, and app filter
  const filteredApps = useMemo(() => {
    let filtered = [...allApps];

    // Filter by selected app
    if (selectedApp && selectedApp.value !== "all") {
      filtered = filtered.filter((app) => app.app_slug === selectedApp.value);
    }

    // Filter by platform (android or ios)
    if (selectedPlatform && selectedPlatform.value !== "all") {
      const platformValue = String(selectedPlatform.value).toLowerCase();
      filtered = filtered.filter(
        (app) => (app.platform || "").toLowerCase() === platformValue
      );
    }

    // Filter by status
    if (selectedStatus && selectedStatus?.value !== "all") {
      filtered = filtered.filter((app) => {
        const percentage = app.without_tax.total_cost_usd_net_percentage * 100; // Multiply by 100 before comparison
        const [minThreshold, maxThreshold] = growthIndicatorRange;
        switch (selectedStatus.value) {
          case "success":
            return percentage > maxThreshold;
          case "warning":
            return percentage >= minThreshold && percentage <= maxThreshold;
          case "danger":
            return percentage < minThreshold;
          default:
            return true;
        }
      });
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter((app) => {
        const searchLower = searchQuery.toLowerCase();
        return (
          app?.app_name?.toLowerCase()?.includes(searchLower) ||
          app?.platform?.toLowerCase()?.includes(searchLower) ||
          app?.app_slug?.toLowerCase()?.includes(searchLower)
        );
      });
    }
    return filtered;
  }, [
    searchQuery,
    allApps,
    selectedStatus,
    selectedApp,
    selectedPlatform,
    growthIndicatorRange,
  ]);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleStatusChange = (
    newValue: SingleValue<StatusOptionData> | null
  ) => {
    if (newValue) {
      setSelectedStatus(newValue);
      const params = searchParams;
      if (newValue.value !== "all") {
        params.set("status", newValue.value);
      } else {
        params.delete("status");
      }
      setSearchParams(params);
    } else {
      setSelectedStatus(DYNAMIC_STATUS_OPTIONS[0]);
      const params = searchParams;
      params.delete("status");
      setSearchParams(params);
    }
  };

  // Update app filter handler to update URL
  const handleAppChange = useCallback(
    (newValue: AppOption | null) => {
      setSelectedApp(newValue);
      const params = searchParams;
      if (newValue && newValue.value !== "all") {
        params.set("app", newValue.value);
      } else {
        params.delete("app");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  // Update platform filter handler to update URL
  const handlePlatformChange = useCallback(
    (newValue: ISelectOption | null) => {
      setSelectedPlatform(newValue);
      const params = searchParams;
      if (newValue && newValue.value && newValue.value !== "all") {
        params.set("platform", newValue.value);
      } else {
        params.delete("platform");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DATA_RANGE_FROM,
      startDate.toISOString()
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DATA_RANGE_TO,
      endDate.toISOString()
    );
    setDateRange(startDate, endDate);
    setChartDateRange({ startDate, endDate });
  };

  const handlePresetChange = (preset: IDateRangePreset | undefined) => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET,
      preset?.id || ""
    );
    setSelectedPreset(preset);
    setSelectedPresetForChart(preset);
  };

  const handleChartClick = (app: IAdmobApp) => {
    setSelectedAppForChart(app);
    setShowChartModal(true);
  };

  const handleChartDateRangeChange = (startDate: Date, endDate: Date) => {
    setChartDateRange({ startDate, endDate });
  };

  // Add filter clear handlers
  const handleClearFilter = useCallback(
    (type: "app" | "status" | "platform") => {
      const params = new URLSearchParams(searchParams);
      switch (type) {
        case "app":
          setSelectedApp(null);
          params.delete("app");
          setSearchParams(params);
          break;
        case "status":
          setSelectedStatus(DYNAMIC_STATUS_OPTIONS[0]);
          params.delete("status");
          setSearchParams(params);
          break;
        case "platform":
          setSelectedPlatform(null);
          params.delete("platform");
          setSearchParams(params);
          break;
      }
    },
    [searchParams, setSearchParams]
  );

  // Add reset all filters handler
  const handleResetAllFilters = useCallback(() => {
    setSelectedApp(null);
    setSelectedStatus(DYNAMIC_STATUS_OPTIONS[0]);
    setSelectedPlatform(null);
    setSearchQuery("");
    setOpenFilter(false);
    const params = new URLSearchParams(searchParams);
    params.delete("app");
    params.delete("status");
    params.delete("platform");
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  // Checking if any filters are applied
  const isFilterApplied = useMemo(() => {
    const hasStatus = selectedStatus && selectedStatus.value !== "all";
    const hasPlatform =
      selectedPlatform &&
      selectedPlatform.value &&
      selectedPlatform.value !== "all";
    return !!(selectedApp || hasStatus || hasPlatform);
  }, [selectedApp, selectedStatus, selectedPlatform]);

  const activeFilters = useMemo(() => {
    const filters: ActiveFilter[] = [];
    if (selectedApp && selectedApp.value !== "all") {
      filters.push({
        id: "app",
        label: selectedApp.label,
        onClear: () => handleClearFilter("app"),
      });
    }
    if (selectedStatus && selectedStatus.value !== "all") {
      filters.push({
        id: "status",
        label: selectedStatus.label,
        onClear: () => handleClearFilter("status"),
      });
    }
    if (selectedPlatform && selectedPlatform.value && selectedPlatform.value !== "all") {
      filters.push({
        id: "platform",
        label: selectedPlatform.value === "android" ? "Android" : selectedPlatform.value === "ios" ? "iOS" : selectedPlatform.value,
        onClear: () => handleClearFilter("platform"),
      });
    }
    return filters;
  }, [selectedApp, selectedStatus, selectedPlatform, handleClearFilter]);

  // Initialize selectedApp when apps data is loaded
  useEffect(() => {
    const appSlug = searchParams.get("app");
    if (!appSlug || allApps.length === 0) return;

    const app = allApps.find((a) => a.app_slug === appSlug);
    if (!app) return;

    setSelectedApp({
      label: app.app_name,
      value: app.app_slug,
      platform: app.platform,
      appId: app.app_slug,
      app_logo_url: app.logo_url,
      app_platform: app.platform,
    });
  }, [allApps, searchParams, setSearchParams]);

  // Initialize platform from URL param
  useEffect(() => {
    const platform = searchParams.get("platform");
    if (!platform) {
      setSelectedPlatform(null);
      return;
    }
    const allowed = ["android", "ios", "all"];
    if (allowed.includes(platform)) {
      setSelectedPlatform({
        label:
          platform === "android"
            ? "Android"
            : platform === "ios"
              ? "iOS"
              : platform,
        value: platform,
      });
    } else {
      setSelectedPlatform(null);
    }
  }, [searchParams]);

  // Initialize status filter from URL param
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      const status = DYNAMIC_STATUS_OPTIONS.find(
        (opt) => opt.value === statusParam
      );
      if (status) {
        setSelectedStatus(status);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (showChartModal && chartError) {
      toast.error(chartError.message);
      setShowChartModal(false);
    }
  }, [chartError, showChartModal]);

  const hasAppDetailsReadPermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.APP_FIND_ONE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const hasAppChartReadPermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.APP_CHART_READ,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const clickableClassesForApp = useMemo(() => {
    return hasAppDetailsReadPermission
      ? "cursor-pointer transition-colors ease hover:text-primary-600"
      : "";
  }, [hasAppDetailsReadPermission]);

  const [columnOptions, setColumnOptions] = useState<ColumnOption[]>([
    {
      id: columnIds.APP_NAME,
      label: t("apps.columns.name"),
      enabled: columnConfig[columnIds.APP_NAME] ?? true,
    },
    // {
    //   id: columnIds.APP_STATUS,
    //   label: t("app.columns.status"),
    //   enabled: columnConfig[columnIds.APP_STATUS] ?? true,
    // },
    {
      id: columnIds.ACCOUNT,
      label: t("apps.columns.account"),
      enabled: columnConfig[columnIds.ACCOUNT] ?? true,
    },
    {
      id: columnIds.ESTIMATED_REVENUE,
      label: t("apps.columns.estimatedRevenueDollar"),
      enabled: columnConfig[columnIds.ESTIMATED_REVENUE] ?? true,
    },
    {
      id: columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS,
      label: t("apps.columns.inAppPurchasesOrSubscriptions"),
      enabled:
        columnConfig[columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS] ?? true,
    },
    {
      id: columnIds.REVENUE_CURRENCY,
      label: t("apps.columns.revenueCurrency"),
      enabled: columnConfig[columnIds.REVENUE_CURRENCY] ?? true,
    },
    {
      id: columnIds.TOTAL_COST_DOLLAR_WITHOUT_TAX,
      label: t("apps.columns.totalCostDollar"),
      enabled: columnConfig[columnIds.TOTAL_COST_DOLLAR_WITHOUT_TAX] ?? true,
    },
    {
      id: columnIds.TOTAL_COST_INR_WITHOUT_TAX,
      label: t("apps.columns.totalCost"),
      enabled: columnConfig[columnIds.TOTAL_COST_INR_WITHOUT_TAX] ?? true,
    },
    {
      id: columnIds.COST_CURRENCY,
      label: t("apps.columns.costCurrency"),
      enabled: columnConfig[columnIds.COST_CURRENCY] ?? true,
    },
    {
      id: columnIds.NET_DOLLAR_WITHOUT_TAX,
      label: t("apps.columns.netDollar"),
      enabled: columnConfig[columnIds.NET_DOLLAR_WITHOUT_TAX] ?? true,
    },
    {
      id: columnIds.PERCENTAGE_WITHOUT_TAX,
      label: t("apps.columns.percentage"),
      enabled: columnConfig[columnIds.PERCENTAGE_WITHOUT_TAX] ?? true,
    },
    {
      id: columnIds.TOTAL_COST_DOLLAR_WITH_TAX,
      label: t("apps.columns.totalCostDollarWithTax"),
      enabled: columnConfig[columnIds.TOTAL_COST_DOLLAR_WITH_TAX] ?? false,
    },
    {
      id: columnIds.TOTAL_COST_WITH_TAX,
      label: t("apps.columns.totalCostWithTax"),
      enabled: columnConfig[columnIds.TOTAL_COST_WITH_TAX] ?? false,
    },
    {
      id: columnIds.NET_DOLLAR_WITH_TAX,
      label: t("apps.columns.netWithTax"),
      enabled: columnConfig[columnIds.NET_DOLLAR_WITH_TAX] ?? false,
    },
    {
      id: columnIds.PERCENTAGE_WITH_TAX,
      label: t("apps.columns.percentageWithTax"),
      enabled: columnConfig[columnIds.PERCENTAGE_WITH_TAX] ?? false,
    },
    {
      id: columnIds.ACTIONS,
      label: t("apps.columns.actions"),
      enabled: columnConfig[columnIds.ACTIONS] ?? true,
    },
  ]);

  // Update column labels whenever language changes so modal reflects current translations
  useEffect(() => {
    setColumnOptions((prev) =>
      prev.map((col) => {
        let updatedLabel = col.label;
        switch (col.id) {
          case columnIds.APP_NAME:
            updatedLabel = t("apps.columns.name");
            break;
          case columnIds.APP_STATUS:
            updatedLabel = t("apps.columns.status");
            break;
          case columnIds.ACCOUNT:
            updatedLabel = t("apps.columns.account");
            break;
          case columnIds.ESTIMATED_REVENUE:
            updatedLabel = t("apps.columns.estimatedRevenueDollar");
            break;
          case columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS:
            updatedLabel = t("apps.columns.inAppPurchasesOrSubscriptions");
            break;
          case columnIds.REVENUE_CURRENCY:
            updatedLabel = t("apps.columns.revenueCurrency");
            break;
          case columnIds.TOTAL_COST_DOLLAR_WITHOUT_TAX:
            updatedLabel = t("apps.columns.totalCostDollar");
            break;
          case columnIds.TOTAL_COST_INR_WITHOUT_TAX:
            updatedLabel = t("apps.columns.totalCost");
            break;
          case columnIds.COST_CURRENCY:
            updatedLabel = t("apps.columns.costCurrency");
            break;
          case columnIds.NET_DOLLAR_WITHOUT_TAX:
            updatedLabel = t("apps.columns.netDollar");
            break;
          case columnIds.PERCENTAGE_WITHOUT_TAX:
            updatedLabel = t("apps.columns.percentage");
            break;
          case columnIds.TOTAL_COST_DOLLAR_WITH_TAX:
            updatedLabel = t("apps.columns.totalCostDollarWithTax");
            break;
          case columnIds.TOTAL_COST_WITH_TAX:
            updatedLabel = t("apps.columns.totalCostWithTax");
            break;
          case columnIds.NET_DOLLAR_WITH_TAX:
            updatedLabel = t("apps.columns.netWithTax");
            break;
          case columnIds.PERCENTAGE_WITH_TAX:
            updatedLabel = t("apps.columns.percentageWithTax");
            break;
          case columnIds.ACTIONS:
            updatedLabel = t("apps.columns.actions");
            break;
        }
        return { ...col, label: updatedLabel };
      })
    );
  }, [i18n.language, t]);

  const handleColumnToggle = (columnIds: string[], checked?: boolean) => {
    const clonedColumnConfig = [...columnOptions];
    for (let i = 0; i < clonedColumnConfig.length; i++) {
      if (columnIds.includes(clonedColumnConfig[i].id)) {
        clonedColumnConfig[i].enabled =
          checked !== undefined ? checked : !clonedColumnConfig[i].enabled;
      }
    }
    setColumnOptions(clonedColumnConfig);

    updatePreferences({
      app_list: {
        ...appListPreference,
        column_config: clonedColumnConfig.reduce((acc, col) => {
          acc[col.id] = col.enabled;
          return acc;
        }, {} as Record<string, boolean>),
      },
    });
  };

  const columns: ColumnDef<IAdmobApp>[] = useMemo(() => {
    const baseColumns: ColumnDef<IAdmobApp>[] = [
      {
        id: columnIds.APP_NAME,
        accessorKey: "app_name",
        header: t("apps.columns.name"),
        size: 250,
        enablePinning: true,
        cell: ({ row }) => {
          const app = row.original;
          return (
            <div
              className={`flex items-center gap-2 relative  sm:gap-3 ${clickableClassesForApp}`}
              onClick={
                clickableClassesForApp
                  ? () =>
                    navigate(`/apps/${app.app_slug}?account=${app.user_name}`)
                  : undefined
              }
            >
              <div>
                {app?.platform?.toLowerCase() === "android" ? (
                  <AndroidLogo
                    weight="fill"
                    className="w-4 h-4 text-green-400"
                  />
                ) : app?.platform?.toLowerCase() === "ios" ? (
                  <AppleLogo weight="fill" className="w-4 h-4 text-gray-700" />
                ) : null}
              </div>
              <div className="min-w-10 w-10 min-h-10 h-10 flex justify-center items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <AppLogo
                  logoUrl={app.logo_url}
                  platform={app.platform}
                  appName={app.app_name}
                />
              </div>
              <p className="text-sm font-medium break-words line-clamp-3">
                {app.app_name}
              </p>
            </div>
          );
        },
      },
      {
        id: columnIds.APP_STATUS,
        accessorKey: "google_admob_app_status",
        header: t("apps.columns.status"),
        size: 100,
        cell: ({ row }) => {
          const status = row.original.google_admob_app_status;
          if (!status) return <span className="text-gray-400">-</span>;

          const getStatusColor = (status: string) => {
            switch (status?.toLowerCase()) {
              case "active":
                return "success";
              case "hidden":
                return "gray";
              case "deleted":
                return "failure";
              default:
                return "gray";
            }
          };
          return (
            <div className="flex items-center gap-2">
              <Badge color={getStatusColor(status || "")} size="xs" className="font-bold rounded-md">
                {t(`common.status.${status?.toLowerCase() || "unknown"}`)}
              </Badge>
            </div>
          );
        },
      },
      {
        id: columnIds.ACCOUNT,
        accessorKey: "user_name",
        header: t("apps.columns.account"),
        size: 130,
        cell: ({ row }) => (
          <div
            className="relative text-xs sm:text-sm break-words whitespace-normal line-clamp-2 text-ellipsis"
            title={row.original.user_name || ""}
          >
            {row.original.user_name || "-"}
          </div>
        ),
      },
      {
        id: columnIds.ESTIMATED_REVENUE,
        accessorKey: "actual_estimate_revenue",
        header: t("apps.columns.estimatedRevenue"),
        size: 155,
        meta: { headerAlign: "right" },
        cell: ({ row }) => {
          const app = row.original;
          const value = app.actual_estimate_revenue;
          const revenueSourceKeys = Object.keys(app.network_wise_revenue || {});
          const entries = getNetworkWiseRevenue(app.network_wise_revenue || {});

          const hasNetworkWiseRevenue = !!app.network_wise_revenue && revenueSourceKeys.length >= 1 && entries.length >= 2;
          return (
            <div className="w-full flex items-center justify-end relative">
              {hasNetworkWiseRevenue ? (
                <Tooltip
                  arrow={true}
                  placement="auto"
                  content={
                    <div className="flex flex-col gap-2 min-w-56">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-6">
                          <span className="text-xs font-semibold">Network:</span>
                          <span className="text-xs text-emerald-400 font-semibold tabular-nums">
                            {Number.isFinite(Number(entries?.[0]?.amount))
                              ? formatNumber(Number(entries?.[0]?.amount))
                              : "0.00"}
                          </span>
                        </div>

                        {entries.length > 1 ? (
                          <div className="pl-3 border-l-2 border-emerald-500 flex flex-col gap-1">
                            {entries.slice(1).map((e) => (
                              <div
                                key={e.network}
                                className="flex items-center justify-between gap-6"
                              >
                                <span className="text-xs">
                                  {String(e.network).endsWith(":")
                                    ? e.network
                                    : `${e.network}:`}
                                </span>
                                <span className="text-xs tabular-nums">
                                  {Number.isFinite(Number(e.amount))
                                    ? formatNumber(Number(e.amount))
                                    : "0.00"}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  }
                >
                  <div className="inline-flex items-center justify-end gap-1 tabular-nums w-full">
                    <span className="text-sm whitespace-nowrap text-right block">
                      {isNaN(value) ? "0.00" : formatNumber(value)}
                    </span>
                    <span className="inline-flex w-[14px] justify-end">
                      <Info
                        size={14}
                        className="text-gray-400 hover:text-gray-600"
                      />
                    </span>
                  </div>
                </Tooltip>
              ) : (
                <div className="inline-flex items-center justify-end gap-1 tabular-nums w-full">
                  <span className="text-sm whitespace-nowrap text-right block">
                    {isNaN(value) ? "0.00" : formatNumber(value)}
                  </span>
                  <span className="inline-flex w-[14px] justify-end invisible">
                    <Info size={14} className="text-gray-400" />
                  </span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS,
        accessorKey: "in_app_purchase.total_in_app_purchase_proceeds",
        header: () => (
          <div className="flex items-center justify-end gap-1 relative">
            <span>{t("apps.columns.inAppPurchasesOrSubscriptions")}</span>
            <Tooltip content="This is Net Amount">
              <Info size={14} className="text-gray-400 hover:text-gray-600" />
            </Tooltip>
          </div>
        ),
        size: 170,
        meta: { headerAlign: "right" },
        cell: ({ row }) => {
          const inAppRevenue = row.original.in_app_purchase;
          return (
            <span className="text-xs sm:text-sm whitespace-nowrap text-right block">
              {isNaN(inAppRevenue.total_in_app_purchase_proceeds)
                ? "0.00"
                : formatNumber(inAppRevenue.total_in_app_purchase_proceeds)}
            </span>
          );
        },
      },
      {
        id: columnIds.REVENUE_CURRENCY,
        accessorKey: "admob_currency",
        header: t("apps.columns.revenueCurrency"),
        size: 80,
        meta: { headerAlign: "right" },
        cell: ({ row }) => (
          <span className="text-xs sm:text-sm whitespace-nowrap text-right block">
            {row.original.admob_currency}
          </span>
        ),
      },
      {
        id: columnIds.TOTAL_COST_DOLLAR_WITHOUT_TAX,
        accessorKey: "without_tax.total_cost_usd",
        header: t("apps.columns.totalCostDollar"),
        size: 120,
        meta: { headerAlign: "right" },
        cell: ({ row }) => {
          const value = row.original.without_tax.total_cost_usd;
          return (
            <span className="text-xs sm:text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        id: columnIds.TOTAL_COST_INR_WITHOUT_TAX,
        accessorKey: "without_tax.total_cost_inr",
        header: t("apps.columns.totalCost"),
        size: 120,
        meta: { headerAlign: "right" },
        cell: ({ row }) => {
          const value = row.original.without_tax.total_cost_inr;
          return (
            <span className="text-xs sm:text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        id: columnIds.COST_CURRENCY,
        accessorKey: "ads_currency",
        header: t("apps.columns.costCurrency"),
        size: 80,
        meta: { headerAlign: "right" },
        cell: ({ row }) => (
          <span className="text-xs sm:text-sm whitespace-nowrap text-right block">
            {row.original.ads_currency}
          </span>
        ),
      },
      {
        id: columnIds.NET_DOLLAR_WITHOUT_TAX,
        accessorKey: "without_tax.total_cost_usd_net",
        header: t("apps.columns.netDollar"),
        size: 120,
        meta: { headerAlign: "right" },
        cell: ({ row }) => {
          const value = row.original.without_tax.total_cost_usd_net;
          return (
            <span className="text-xs sm:text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        id: columnIds.PERCENTAGE_WITHOUT_TAX,
        accessorKey: "without_tax.total_cost_usd_net_percentage",
        header: t("apps.columns.percentage"),
        size: 100,
        meta: { headerAlign: "right" },
        cell: ({ row }) => {
          const percentage =
            row.original.without_tax.total_cost_usd_net_percentage;
          let color = "gray";
          if (isNaN(percentage)) {
            return (
              <Badge
                color="gray"
                size="sm"
                className="whitespace-nowrap ml-auto"
              >
                0.00%
              </Badge>
            );
          }
          const percentageValue = percentage * 100; // Multiply by 100 before comparison
          const [minThreshold, maxThreshold] = growthIndicatorRange;
          if (percentageValue < minThreshold) color = "failure";
          else if (
            percentageValue >= minThreshold &&
            percentageValue <= maxThreshold
          )
            color = "warning";
          else if (percentageValue > maxThreshold) color = "success";
          return (
            <Badge
              color={color}
              size="sm"
              className="whitespace-nowrap ml-auto"
            >
              {formatNumber(percentage * 100)}%
            </Badge>
          );
        },
      },
      // With Tax columns
      {
        id: columnIds.TOTAL_COST_DOLLAR_WITH_TAX,
        accessorKey: "with_tax.total_cost_usd",
        header: t("apps.columns.totalCostDollarWithTax"),
        size: 130,
        meta: { headerAlign: "right" },
        cell: ({ row }) => {
          const value = row.original.with_tax.total_cost_usd;
          return (
            <span className="text-xs sm:text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        id: columnIds.TOTAL_COST_WITH_TAX,
        accessorKey: "with_tax.total_cost_inr",
        header: t("apps.columns.totalCostWithTax"),
        size: 120,
        meta: { headerAlign: "right" },
        cell: ({ row }) => {
          const value = row.original.with_tax.total_cost_inr;
          return (
            <span className="text-xs sm:text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        id: columnIds.NET_DOLLAR_WITH_TAX,
        accessorKey: "with_tax.total_cost_usd_net",
        header: t("apps.columns.netWithTax"),
        size: 120,
        meta: { headerAlign: "right" },
        cell: ({ row }) => {
          const value = row.original.with_tax.total_cost_usd_net;
          return (
            <span className="text-xs sm:text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        id: columnIds.PERCENTAGE_WITH_TAX,
        accessorKey: "with_tax.total_cost_usd_net_percentage",
        header: t("apps.columns.percentageWithTax"),
        size: 160,
        meta: { headerAlign: "right" },
        cell: ({ row }) => {
          const percentage =
            row.original.with_tax.total_cost_usd_net_percentage;
          let color = "gray";
          if (isNaN(percentage)) {
            return (
              <Badge
                color="gray"
                size="sm"
                className="whitespace-nowrap ml-auto"
              >
                0.00%
              </Badge>
            );
          }
          const percentageValue = percentage * 100; // Multiply by 100 before comparison
          const [minThreshold, maxThreshold] = growthIndicatorRange;
          if (percentageValue < minThreshold) color = "failure";
          else if (
            percentageValue >= minThreshold &&
            percentageValue <= maxThreshold
          )
            color = "warning";
          else if (percentageValue > maxThreshold) color = "success";
          return (
            <Badge
              color={color}
              size="sm"
              className="whitespace-nowrap ml-auto"
            >
              {formatNumber(percentage * 100)}%
            </Badge>
          );
        },
      },
    ];
    if (hasAppChartReadPermission) {
      baseColumns.push({
        id: columnIds.ACTIONS,
        size: 70,
        header: t("apps.columns.actions"),
        cell: ({ row }) => {
          const app = row.original;
          return (
            <div className="flex gap-2 relative w-full">
              {hasAppChartReadPermission && (
                <Tooltip content={t("apps.appList.revenueChartTooltip")}>
                  <Button
                    size="xs"
                    color="blue"
                    outline
                    className="p-[2px]"
                    onClick={() => handleChartClick(app)}
                  >
                    <ChartBar className="h-5" size={16} />
                  </Button>
                </Tooltip>
              )}
            </div>
          );
        },
      });
    }
    return baseColumns.filter(
      (col) => columnOptions.find((opt) => opt.id === col.id)?.enabled
    );
  }, [
    t,
    hasAppChartReadPermission,
    clickableClassesForApp,
    navigate,
    columnOptions,
    growthIndicatorRange,
    searchQuery,
    filteredApps
  ]);

  return (
    <div>
      {/* Mobile Layout */}
      <div className="flex flex-col gap-4 sm:hidden">
        <div className="flex items-center justify-between ">
          <div className="flex items-center gap-4 min-h-[37px]">
            <PuzzlePiece size={20} className="text-gray-700" />
            <h1 className="text-xl font-bold text-gray-700">
              {t("apps.title")}
            </h1>
          </div>
          {!isLoading && results?.average_actual_usd && (
            <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md whitespace-nowrap">
              {`${t("appCountryWiseReports.averageCurrencyRate")} = ₹${results.average_actual_usd
                }`}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <SearchInput
            onSearch={handleSearch}
            placeholder={t("common.search")}
            className="w-full"
          />
        </div>
      </div>
      {/* Desktop Layout */}
      <div className="hidden sm:flex sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="pl-2">
          <div className="flex items-center gap-2">
            <AndroidLogo size={20} className="text-gray-700" weight="duotone" />
            <h1 className="text-md font-bold text-gray-700">
              {t("apps.title")}
            </h1>
          </div>
          {isLoading ? (
            <div className="w-[200px] h-4 rounded bg-gray-200 animate-pulse"></div>
          ) : (
            <>
              {results?.average_actual_usd && (
                <div className="text-xs text-gray-600 ">
                  {`${t("appCountryWiseReports.averageCurrencyRate")} = ₹${results.average_actual_usd
                    }`}
                </div>
              )}
            </>
          )}
        </div>
        <div className="w-auto flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <AppListFilters
              t={t}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              selectedStatus={selectedStatus}
              onStatusChange={handleStatusChange}
              onApplyFilter={() => setOpenFilter(false)}
              selectedApp={selectedApp}
              onAppChange={handleAppChange}
              apps={allApps}
              selectedPlatform={selectedPlatform}
              onPlatformChange={handlePlatformChange}
              growthIndicatorRange={growthIndicatorRange}
            />
          </div>
          <div className="flex gap-2 items-center">
            <DateRangePickerButton
              onDateRangeChange={handleDateRangeChange}
              onPresetChange={handlePresetChange}
              initialStartDate={dateRange.startDate}
              initialEndDate={dateRange.endDate}
              initialPreset={selectedPreset?.id}
            />
          </div>

          <div className="">
            <SearchInput
              onSearch={handleSearch}
              placeholder={t("common.search")}
              className="w-[250px] "
              style={{ height: "38px" }}
              size="md"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              color="light"
              size="sm"
              onClick={() => setShowColumnSelector(true)}
            >
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                {/* <p className="text-sm">{t("common.columnsText")}</p> */}
              </div>
            </Button>
            <CustomSwitch
              checked={showWithTax}
              onChange={(checked) => {
                handleColumnToggle(withTaxColumns, checked);
              }}
              label={t("common.withTax")}
              color="indigo"
            />
          </div>
        </div>
      </div>

      {/* Add Filter Summary */}
      <FilterSummary
        filters={activeFilters}
        onClearAll={handleResetAllFilters}
        title={t("common.appliedFilters")}
        clearAllLabel={t("common.resetAllFilters")}
      />

      <div className="overflow-x-auto rounded-lg mt-2">
        <Table
          data={filteredApps}
          columns={columns}
          isLoading={isLoading}
          showPagination={true}
          showSearch={false}
          loadingHeight={4}
          loadingRowCount={11}
          pageSize={DEFAULT_ITEMS_PER_PAGE}
          maxHeight={`calc(100vh - ${(isMobile ? 330 : isFilterApplied ? 263 : isLoading ? 155 : 213) +
            BannerHeightOffset
            }px)`}
          isMobile={isMobile}
          summaryKeys={[
            { key: columnIds.APP_NAME, customValue: "SUMMARY" },
            { key: columnIds.ESTIMATED_REVENUE },
            { key: columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS },
            { key: columnIds.TOTAL_COST_DOLLAR_WITHOUT_TAX },
            { key: columnIds.TOTAL_COST_INR_WITHOUT_TAX },
            { key: columnIds.NET_DOLLAR_WITHOUT_TAX },
            { key: columnIds.TOTAL_COST_DOLLAR_WITH_TAX },
            { key: columnIds.TOTAL_COST_WITH_TAX },
            { key: columnIds.NET_DOLLAR_WITH_TAX },
          ]}
          summaryPosition="bottom"
          sortByKey={columnIds.ESTIMATED_REVENUE}
          sortOrder="desc"
          leftPinnedColumnIds={[columnIds.APP_NAME]}
        />
        <BannerNote
          note={t("common.googleDataNote")}
          title={t("common.dataSourceInfo")}
          marqueeSpeed={25}
          delay={3}
        />
      </div>
      {/* Column Selector Modal */}
      <AnimatedModal
        show={showColumnSelector}
        onClose={() => setShowColumnSelector(false)}
        title={t("apps.columnSelector.title")}
        size="sm"
      >
        <div className="space-y-4 p-4">
          {columnOptions.map((column) => (
            <div key={column.id} className="flex items-center justify-between">
              <span>{column.label}</span>
              <CustomSwitch
                checked={column.enabled}
                onChange={() => handleColumnToggle([column.id])}
              />
            </div>
          ))}
        </div>
      </AnimatedModal>
      {/* Chart Modal */}
      <AnimatedModal
        show={showChartModal}
        onClose={() => {
          setSelectedAppForChart(null);
          setShowChartModal(false);
          setSelectedPresetForChart(selectedPreset);
          handleChartDateRangeChange(dateRange.startDate, dateRange.endDate);
        }}
        title={`${selectedAppForChart?.app_name || ""} - ${t(
          "apps.columns.estimatedRevenue"
        )} vs ${t("apps.columns.totalCost")}`}
        size="2xl"
        bodyClassName="p-4"
      >
        <AppRevenueChart
          key={`${selectedPresetForChart}|${JSON.stringify(chartDateRange)}`}
          data={chartData || []}
          onDateRangeChange={handleChartDateRangeChange}
          setSelectedPresetForChart={setSelectedPresetForChart}
          loading={isLoadingChartData}
          dateRange={chartDateRange}
          selectedPresetForChart={selectedPresetForChart}
        />
      </AnimatedModal>
    </div>
  );
};

export default AppList;
